/**
 * Presentation Layer - Query Provider
 * Wraps the application with React Query support
 */

"use client";

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { siteConfig } from "@/config/site";
import { LIMITS } from "@/constants";
import type { ErrorWithStatus } from "@/shared/data/api";

export function QueryProvider({ children }: { children: React.ReactNode }) {
  // Create a new QueryClient instance for each session
  // This ensures that data isn't shared between users and browser sessions
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: siteConfig.queryStaleTime,
            gcTime: siteConfig.queryGcTime,

            retry: (failureCount, error) => {
              const err = error as ErrorWithStatus;
              if (
                err instanceof Error &&
                typeof err.statusCode === "number" &&
                err.statusCode >= 400 &&
                err.statusCode < 500
              ) {
                return false;
              }
              return failureCount < LIMITS.MAX_QUERY_RETRIES;
            },

            refetchOnMount: true,
            refetchOnReconnect: true,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {!siteConfig.isProduction && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}
